import React, { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, { SelectChangeEvent, selectClasses } from '@mui/material/Select'
import { capitalizeFirstCharacter } from '@cbgms/base/utils/helpers'
import { KeyType, isKeyType } from '@cbgms/base/utils/keyevent'
import { useTopSearchContext } from '../../context/TopSearchContext'
import { ESearchFilterValue } from '../../topSearchUtils'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      minWidth: 120,
      [`& .${selectClasses.icon}`]: {
        color: theme.palette.text.secondary
      }
    },
    select: {
      color: theme.palette.text.secondary
    }
  })
)

interface Props {
  onChange?: (selectedFilter: ESearchFilterValue) => void
}

const _SearchDropdown: React.FC<Props> = ({ onChange }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { searchFilter, setSearchFilter, menuOptions } = useTopSearchContext()

  const handleChange = useCallback(
    (event: SelectChangeEvent<ESearchFilterValue>) => {
      onChange && onChange(event.target.value as ESearchFilterValue)
      setSearchFilter(event.target.value as ESearchFilterValue)
    },
    [onChange, setSearchFilter]
  )

  const stopBubbling = useCallback((event: React.KeyboardEvent) => {
    if (isKeyType(event, KeyType.ArrowDownKey) || isKeyType(event, KeyType.ArrowUpKey)) {
      event.stopPropagation()
    }
  }, [])

  return (
    <FormControl variant='outlined' className={classes.formControl} data-testid='main-search-dropdown'>
      <Select
        sx={{
          borderLeft: '1px solid rgba(0,0,0,0.2)',
          borderRadius: 0,
          margin: 0,
          ml: 0.5,
          pl: '0px !important',
          [`& .${selectClasses.select}`]: { py: 1 }
        }}
        className={classes.select}
        labelId='simple-select-label'
        id='simple-select'
        value={searchFilter}
        onChange={handleChange}
        MenuProps={{ onKeyDown: stopBubbling }}
      >
        {menuOptions.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {capitalizeFirstCharacter(t(option.translateKey))}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
const SearchDropdown = memo(_SearchDropdown)
export default SearchDropdown
